/**
 * Task 列表页筛选条件 ↔ URL query 的编解码。
 *
 * 目的:分享链接即分享查询条件 —— 对方打开链接直接看到同一份筛选结果,
 * 而不是空列表 + 口头描述「你再自己选一下状态和评测器」。
 *
 * 两套 keyset,因为普通列表页与 ?mode=compare 对比页是两套独立筛选,且有同名
 * 但语义不同的字段(如 diffMode:普通页是 Daily 下钻差异模式,对比页是矩阵象限
 * 联动)。对比页统一加 `c_` 前缀隔离,避免互相污染:
 *   - 普通页: ?sceneName=xx&statuses=10,20&page=2
 *   - 对比页: ?mode=compare&compareJobId=88&c_sceneName=xx&c_diffMode=new_fail
 *
 * 编解码只接管自己声明的 key(见 TASK_FILTER_CODECS / COMPARE_FILTER_CODECS),
 * 其余 query(mode / compareJobId / diffMode / taskStatus 等由别处管理的参数)
 * 原样保留 —— 写回时基于当前 searchParams 增量改,不整体覆盖。
 */
import type {
  TaskListParams,
  TaskDiffMode,
  CompareConsistency,
  BehaviorFilterValue,
  MetricCompare,
  MetricSort,
  ReasonDiff,
  ReasonKind,
  ResponsibilityFilter,
  EvaluatorStatusFilter,
} from '../../apis/simulation';
import type { CommentFilterValue } from '../../components/CommentFilterPopover';
import { EMPTY_COMMENT_FILTER } from '../../components/CommentFilterPopover';
import { SCENE_REGION_VALUES } from '../../constants/region';
import { decodeSceneSetId, decodeSceneSetVerdict, normalizeSceneSetFilter } from './sceneSetFilter';

/** 单个 query 值的编解码。decode 返回 undefined 表示「该值非法,按未设置处理」。 */
interface Codec<T> {
  encode: (v: T) => string;
  decode: (raw: string) => T | undefined;
}

const strCodec: Codec<string> = {
  encode: (v) => v,
  // 空串视为未设置:URL 上 ?sceneName= 不该让列表进入「按空串筛选」
  decode: (raw) => (raw === '' ? undefined : raw),
};

/** 正整数 codec —— 给 ID 类字段用。0 / 负数是脏 URL,按未设置处理而非发给后端空查。 */
const idCodec: Codec<number> = {
  encode: (v) => String(v),
  decode: (raw) => {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  },
};

const boolCodec: Codec<boolean> = {
  encode: (v) => (v ? '1' : '0'),
  decode: (raw) => {
    if (raw === '1' || raw === 'true') return true;
    if (raw === '0' || raw === 'false') return false;
    return undefined;
  },
};

/** 数字数组用逗号连接。非法项整条丢弃,避免把 NaN 传给后端。 */
const numArrCodec: Codec<number[]> = {
  encode: (v) => v.join(','),
  decode: (raw) => {
    if (raw === '') return undefined;
    const parts = raw.split(',').map((s) => Number(s.trim()));
    if (parts.some((n) => !Number.isFinite(n))) return undefined;
    return parts;
  },
};

/** 枚举值 codec:不在白名单内的值按未设置处理,防止脏 URL 把非法值送到后端。 */
function enumCodec<T extends string>(allowed: readonly T[]): Codec<T> {
  const set: ReadonlySet<string> = new Set(allowed);
  return {
    encode: (v) => v,
    decode: (raw) => (set.has(raw) ? (raw as T) : undefined),
  };
}

/** 枚举值数组 codec(如 regions)：逗号连接；逐项按白名单过滤，
 *  非法项丢弃而不是整条判废 —— 「cn,xx」仍能筛出国内，脏 URL 不至于让筛选全丢。
 *  全部非法/为空 → 按未设置处理（= 不过滤，绝不能兜底成 cn）。 */
function enumArrCodec<T extends string>(allowed: readonly T[]): Codec<T[]> {
  const set: ReadonlySet<string> = new Set(allowed);
  return {
    encode: (v) => v.join(','),
    decode: (raw) => {
      if (raw === '') return undefined;
      const parts = raw
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter((s) => set.has(s)) as T[];
      return parts.length > 0 ? Array.from(new Set(parts)) : undefined;
    },
  };
}

/** status 后端接受 string | number;URL 上能转数字就转,否则保留原字符串。 */
const statusCodec: Codec<string | number> = {
  encode: (v) => String(v),
  decode: (raw) => {
    if (raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : raw;
  },
};

const DIFF_MODES = [
  'new_success', 'new_fail',
  'pass_pass', 'pass_fail', 'fail_pass', 'fail_fail',
  'all',
] as const;
const CONSISTENCIES = ['same', 'diff', 'only_current'] as const;
const BEHAVIOR_STRONGS = [
  'ANY', 'TERMINATION_DIFF', 'DURATION_DIFF', 'ONE_SIDE_CONTACT', 'STOP_COUNT_DIFF', 'JUNCTION_COUNT_DIFF', 'CLOSE_CROSS_05',
] as const;
const BEHAVIOR_LABELS = [
  'NEEDS_REVIEW', 'SIGNIFICANT', 'SLIGHT', 'CONSISTENT', 'INCONCLUSIVE', 'MISMATCH',
] as const;
const METRIC_COMPARES = ['increase', 'decrease', 'equal'] as const;
/** 指标列排序方向:desc = 差值(当前 − 对比)从大到小(前端默认)/ asc = 从小到大。 */
const METRIC_SORTS = ['asc', 'desc'] as const;
/** 原因比对筛选取值,与判定同集合(same/diff/diff_spatial/only_current)。 */
const REASON_DIFFS = ['same', 'diff', 'diff_spatial', 'only_current'] as const;
const RESPONSIBILITIES = ['with', 'without'] as const;
const EVAL_STATUSES = ['pass', 'fail', 'error'] as const;
const CHECK_STATUSES = ['pass', 'fail'] as const;
const COMMENT_SCOPES = ['both', 'title', 'body'] as const;
const COMMENT_COMBINES = ['and', 'or'] as const;

/* ------------------------------------------------------------------ *
 * 普通列表模式
 * ------------------------------------------------------------------ */

/** 普通列表模式下参与 URL 往返的 TaskListParams 字段。
 *
 *  不含:
 *  - jobId —— 在路由 path 里(/job-tasks/:jobId),不重复放 query;
 *  - compareJobId / diffMode —— Daily 下钻筛选,已有独立的 URL 读写逻辑
 *    (diffFilter / clearDiffFilter),这里不接管以免两处互相覆盖;
 *  - evaluatorDiff / diffConsistency / metricCompare / metricSort /
 *    compareComment* / commentCombine —— 仅对比页有意义,走 c_ 前缀那套。
 *
 *  **metricKey 是两页共用的**:stats 页用它选「指标」列(单 Job 也能看参数),
 *  对比页用它选差值列 —— 所以它同时出现在这里的普通 keyset 与对比页的 c_metricKey,
 *  两边各自独立、互不污染(见文件头「两套 keyset」)。
 */
type TaskFilterKey =
  | 'sceneName' | 'status' | 'statuses' | 'taskId' | 'sceneIds'
  | 'evaluatorName' | 'evaluatorStatus' | 'apiCode' | 'metricKey' | 'metricSort'
  | 'reasonKind' | 'reasonCode'
  | 'responsibility' | 'respEventName' | 'checkName' | 'checkStatus'
  | 'label' | 'mapName' | 'regions' | 'treeNodeId' | 'treeNodeIds'
  | 'commentTitle' | 'commentKeyword' | 'commentScope' | 'hasComment'
  | 'sceneSetId' | 'sceneSetVerdict';

/* eslint-disable @typescript-eslint/no-explicit-any -- 逐 key 的 codec 类型各不相同,
   在 map 上统一成 Codec<any>;读写口径由 TaskFilterKey ↔ TaskListParams 保证。 */
const TASK_FILTER_CODECS: Record<TaskFilterKey, Codec<any>> = {
  sceneName: strCodec,
  status: statusCodec,
  statuses: numArrCodec,
  taskId: idCodec,
  sceneIds: numArrCodec,
  evaluatorName: strCodec,
  evaluatorStatus: enumCodec<EvaluatorStatusFilter>(EVAL_STATUSES),
  apiCode: strCodec,
  metricKey: strCodec,
  metricSort: enumCodec<MetricSort>(METRIC_SORTS),
  reasonKind: enumCodec<ReasonKind>(['fail', 'exempt']),
  reasonCode: strCodec,
  responsibility: enumCodec<ResponsibilityFilter>(RESPONSIBILITIES),
  respEventName: strCodec,
  checkName: strCodec,
  checkStatus: enumCodec<'pass' | 'fail'>(CHECK_STATUSES),
  label: strCodec,
  mapName: strCodec,
  regions: enumArrCodec(SCENE_REGION_VALUES),
  treeNodeId: idCodec,
  treeNodeIds: numArrCodec,
  commentTitle: strCodec,
  commentKeyword: strCodec,
  commentScope: enumCodec<'both' | 'title' | 'body'>(COMMENT_SCOPES),
  hasComment: boolCodec,
  // 单 Job 场景集统计下钻：id 允许 0（未归属/不可见），verdict 白名单（见 sceneSetFilter.ts）
  sceneSetId: { encode: (v: number) => String(v), decode: decodeSceneSetId },
  sceneSetVerdict: { encode: (v: string) => v, decode: decodeSceneSetVerdict },
};
/* eslint-enable @typescript-eslint/no-explicit-any */

const TASK_FILTER_KEYS = Object.keys(TASK_FILTER_CODECS) as TaskFilterKey[];

/** 分页 query key —— 沿用页面既有命名(?page= / ?pageSize=),不改以免旧链接失效。 */
const PAGE_KEY = 'page';
const PAGE_SIZE_KEY = 'pageSize';

/** 值是否应视为「未设置」(不写进 URL)。空数组/空串/undefined/null 都算。 */
function isEmpty(v: unknown): boolean {
  if (v === undefined || v === null || v === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

/**
 * 把普通列表模式的 queryParams 写进 URL(增量改,不动其它 query)。
 *
 * 分页:page/pageSize 始终写出,保证「第 3 页」这种上下文可分享;其余 filter
 * 只在非空时写,空值显式 delete —— 用户清掉某个筛选后链接里也要干净,否则
 * 分享出去对方还会看到已被清掉的条件。
 */
export function writeTaskFiltersToParams(
  base: URLSearchParams,
  params: TaskListParams,
): URLSearchParams {
  const sp = new URLSearchParams(base);
  for (const key of TASK_FILTER_KEYS) {
    const value = params[key];
    if (isEmpty(value)) {
      sp.delete(key);
    } else {
      sp.set(key, TASK_FILTER_CODECS[key].encode(value));
    }
  }
  sp.set(PAGE_KEY, String(params.current ?? 1));
  sp.set(PAGE_SIZE_KEY, String(params.pageSize ?? 10));
  return sp;
}

/** 从 URL 读回普通列表模式的 filter 子集(不含 jobId / 分页,各自单独取)。 */
export function readTaskFiltersFromParams(sp: URLSearchParams): Partial<TaskListParams> {
  const out: Partial<TaskListParams> = {};
  for (const key of TASK_FILTER_KEYS) {
    const raw = sp.get(key);
    if (raw == null) continue;
    const decoded = TASK_FILTER_CODECS[key].decode(raw);
    if (decoded === undefined) continue;
    // 逐 key 的值类型已由 TASK_FILTER_CODECS 保证,这里统一赋值
    (out as Record<string, unknown>)[key] = decoded;
  }
  // status(单值)与 statuses(多值)是两套正交口径,后端同传会叠加过滤。
  // 正常写出的链接不会同时带,但历史书签可能残留旧的 ?status=,故读取时
  // 以多值为准丢掉单值,避免「看着只选了多状态、结果被旧单值二次收窄」。
  if (out.statuses !== undefined && out.status !== undefined) {
    delete out.status;
  }
  // 后端要求 sceneSetVerdict 必须与 sceneSetId 同传
  const sceneSet = normalizeSceneSetFilter({ sceneSetId: out.sceneSetId, sceneSetVerdict: out.sceneSetVerdict });
  delete out.sceneSetId;
  delete out.sceneSetVerdict;
  Object.assign(out, sceneSet);
  return out;
}

/** 从 URL 读分页(非法/缺省时给默认值)。 */
export function readPaginationFromParams(
  sp: URLSearchParams,
  defaultPageSize = 10,
): { current: number; pageSize: number } {
  const page = Number(sp.get(PAGE_KEY));
  const pageSize = Number(sp.get(PAGE_SIZE_KEY));
  return {
    current: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : defaultPageSize,
  };
}

/**
 * 从 URL 还原「评论筛选」三档控件的值(CommentFilterPopover 的受控 value)。
 *
 * queryParams 里评论条件是摊平的 commentKeyword/commentScope/hasComment 三个字段,
 * 而 Popover 吃的是合并对象 —— 回填表单时需要这一步反向组装,否则链接打开后
 * 列表已按评论筛选、但 Popover 上不显示任何已选状态(所见非所得)。
 */
export function readCommentFilterFromParams(sp: URLSearchParams): CommentFilterValue {
  const keyword = sp.get('commentKeyword') ?? undefined;
  const scope = enumCodec<'both' | 'title' | 'body'>(COMMENT_SCOPES)
    .decode(sp.get('commentScope') ?? '');
  const hasCommentRaw = sp.get('hasComment');
  const hasComment = hasCommentRaw == null ? undefined : boolCodec.decode(hasCommentRaw);
  if (!keyword && hasComment === undefined) return EMPTY_COMMENT_FILTER;
  return {
    keyword: keyword || undefined,
    scope: scope ?? 'both',
    hasComment,
  };
}

/** 普通列表模式的表单字段(Form.Item name) —— 回填表单时按此挑值。 */
const TASK_FORM_KEYS = [
  'taskId', 'sceneName', 'mapName', 'label', 'regions', 'treeNodeIds', 'apiCode', 'status',
  'evaluatorName', 'evaluatorStatus', 'respEventName', 'responsibility',
  'checkName', 'checkStatus', 'metricKey', 'reasonKind', 'reasonCode',
] as const;

/** 从已解码的 filter 里挑出筛选表单认识的字段,用于 form.setFieldsValue 回填。 */
export function pickFormValues(
  filters: Partial<TaskListParams>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of TASK_FORM_KEYS) {
    const v = filters[key];
    if (!isEmpty(v)) out[key] = v;
  }
  return out;
}

/* ------------------------------------------------------------------ *
 * 任务对比模式 (?mode=compare)
 * ------------------------------------------------------------------ */

/** 对比页的筛选状态集合 —— 与页面上那组 compare* useState 一一对应。 */
export interface CompareFilterState {
  compareSceneSetId?: number;
  sceneName?: string;
  status?: string | number;
  diffMode?: TaskDiffMode;
  diffConsistency?: CompareConsistency;
  /** 行为对比标签筛选（与翻转象限正交取交集） */
  behaviorLabel?: BehaviorFilterValue;
  /** 行为对比强事件筛选（六类强事件 code 或 ANY） */
  behaviorStrong?: string;
  evaluatorName?: string;
  metricKey?: string;
  metricCompare?: MetricCompare;
  metricSort?: MetricSort;
  reasonDiff?: ReasonDiff;
  /** 原因**类别**筛（当前侧 reason.code）；与 reasonDiff 正交，同传取交集 */
  reasonCode?: string;
  comment: CommentFilterValue;
  compareSideComment: CommentFilterValue;
  commentCombine: 'and' | 'or';
  page: number;
  pageSize: number;
}

export const EMPTY_COMPARE_FILTERS: CompareFilterState = {
  comment: EMPTY_COMMENT_FILTER,
  compareSideComment: EMPTY_COMMENT_FILTER,
  commentCombine: 'and',
  page: 1,
  pageSize: 10,
};

/** 对比页 query key 一律 `c_` 前缀 —— 与普通页同名字段隔离(见文件头注释)。 */
const C = (k: string) => `c_${k}`;

/** 把评论筛选三档摊平写进 sp(prefix 区分当前侧 / 对比侧)。 */
function writeComment(sp: URLSearchParams, prefix: string, v: CommentFilterValue) {
  const kw = v.keyword?.trim();
  if (kw) {
    sp.set(C(`${prefix}Keyword`), kw);
    sp.set(C(`${prefix}Scope`), v.scope ?? 'both');
  } else {
    sp.delete(C(`${prefix}Keyword`));
    sp.delete(C(`${prefix}Scope`));
  }
  const hasKey = C(prefix === 'comment' ? 'hasComment' : 'sideHasComment');
  if (v.hasComment === undefined) sp.delete(hasKey);
  else sp.set(hasKey, boolCodec.encode(v.hasComment));
}

function readComment(sp: URLSearchParams, prefix: string): CommentFilterValue {
  const keyword = sp.get(C(`${prefix}Keyword`)) ?? undefined;
  const scope = enumCodec<'both' | 'title' | 'body'>(COMMENT_SCOPES)
    .decode(sp.get(C(`${prefix}Scope`)) ?? '');
  const hasKey = C(prefix === 'comment' ? 'hasComment' : 'sideHasComment');
  const hasRaw = sp.get(hasKey);
  const hasComment = hasRaw == null ? undefined : boolCodec.decode(hasRaw);
  if (!keyword && hasComment === undefined) return EMPTY_COMMENT_FILTER;
  return { keyword: keyword || undefined, scope: scope ?? 'both', hasComment };
}

/** 把对比页筛选写进 URL(增量改;空值删除,保持链接干净)。 */
export function writeCompareFiltersToParams(
  base: URLSearchParams,
  f: CompareFilterState,
): URLSearchParams {
  const sp = new URLSearchParams(base);
  const setOrDel = (key: string, v: unknown, enc: (x: never) => string) => {
    if (isEmpty(v)) sp.delete(key);
    else sp.set(key, enc(v as never));
  };
  setOrDel(C('sceneSetId'), f.compareSceneSetId, String);
  setOrDel(C('sceneName'), f.sceneName, strCodec.encode as (x: never) => string);
  setOrDel(C('status'), f.status, statusCodec.encode as (x: never) => string);
  setOrDel(C('diffMode'), f.diffMode, strCodec.encode as (x: never) => string);
  setOrDel(C('diffConsistency'), f.diffConsistency, strCodec.encode as (x: never) => string);
  setOrDel(C('behaviorLabel'), f.behaviorLabel, strCodec.encode as (x: never) => string);
  setOrDel(C('behaviorStrong'), f.behaviorStrong, strCodec.encode as (x: never) => string);
  setOrDel(C('evaluatorName'), f.evaluatorName, strCodec.encode as (x: never) => string);
  setOrDel(C('metricKey'), f.metricKey, strCodec.encode as (x: never) => string);
  setOrDel(C('metricCompare'), f.metricCompare, strCodec.encode as (x: never) => string);
  setOrDel(C('metricSort'), f.metricSort, strCodec.encode as (x: never) => string);
  setOrDel(C('reasonDiff'), f.reasonDiff, strCodec.encode as (x: never) => string);
  setOrDel(C('reasonCode'), f.reasonCode, strCodec.encode as (x: never) => string);
  writeComment(sp, 'comment', f.comment);
  writeComment(sp, 'sideComment', f.compareSideComment);
  // 组合方式只在非默认时写,避免每条链接都挂一个 c_commentCombine=and 的噪音
  if (f.commentCombine === 'or') sp.set(C('commentCombine'), 'or');
  else sp.delete(C('commentCombine'));
  sp.set(C('page'), String(f.page));
  sp.set(C('pageSize'), String(f.pageSize));
  return sp;
}

/** 从 URL 读回对比页筛选;缺省回落到 EMPTY_COMPARE_FILTERS 的默认值。 */
export function readCompareFiltersFromParams(sp: URLSearchParams): CompareFilterState {
  const rawSceneSetId = sp.get(C('sceneSetId'));
  const sceneSetId = rawSceneSetId != null && /^\d+$/.test(rawSceneSetId) ? Number(rawSceneSetId) : NaN;
  const page = Number(sp.get(C('page')));
  const pageSize = Number(sp.get(C('pageSize')));
  return {
    compareSceneSetId: Number.isSafeInteger(sceneSetId) && sceneSetId >= 0 ? sceneSetId : undefined,
    sceneName: strCodec.decode(sp.get(C('sceneName')) ?? ''),
    status: statusCodec.decode(sp.get(C('status')) ?? ''),
    diffMode: enumCodec<TaskDiffMode>(DIFF_MODES).decode(sp.get(C('diffMode')) ?? ''),
    diffConsistency: enumCodec<CompareConsistency>(CONSISTENCIES)
      .decode(sp.get(C('diffConsistency')) ?? ''),
    behaviorLabel: enumCodec<BehaviorFilterValue>(BEHAVIOR_LABELS)
      .decode(sp.get(C('behaviorLabel')) ?? ''),
    behaviorStrong: enumCodec<string>(BEHAVIOR_STRONGS).decode(sp.get(C('behaviorStrong')) ?? ''),
    evaluatorName: strCodec.decode(sp.get(C('evaluatorName')) ?? ''),
    metricKey: strCodec.decode(sp.get(C('metricKey')) ?? ''),
    metricCompare: enumCodec<MetricCompare>(METRIC_COMPARES)
      .decode(sp.get(C('metricCompare')) ?? ''),
    metricSort: enumCodec<MetricSort>(METRIC_SORTS)
      .decode(sp.get(C('metricSort')) ?? ''),
    reasonDiff: enumCodec<ReasonDiff>(REASON_DIFFS)
      .decode(sp.get(C('reasonDiff')) ?? ''),
    reasonCode: strCodec.decode(sp.get(C('reasonCode')) ?? ''),
    comment: readComment(sp, 'comment'),
    compareSideComment: readComment(sp, 'sideComment'),
    commentCombine: enumCodec<'and' | 'or'>(COMMENT_COMBINES)
      .decode(sp.get(C('commentCombine')) ?? '') ?? 'and',
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 10,
  };
}

